// src/components/navigation/Navigation.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

// Import the centralized navigation data
import navigationData from '../../data/navigationData';

const Navigation = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-dropdown')) {
        setOpenDropdown(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  
  // Close dropdown when route changes
  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);
  
  const toggleDropdown = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (openDropdown === index) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(index);
    }
  };
  
  // Check if the current path matches or is a child of a nav item
  const isActive = (navPath) => {
    if (!navPath) return false;
    return location.pathname === navPath || location.pathname.startsWith(`${navPath}/`);
  };
  
  return (
    <nav className="hidden md:flex items-center space-x-1">
      {navigationData.map((item, index) => (
        <div key={index} className="relative group nav-dropdown">
          {item.hasDropdown ? (
            <>
              <button 
                onClick={(e) => toggleDropdown(index, e)}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                  isActive(item.path) 
                    ? 'text-white bg-primary-dark' 
                    : 'text-white hover:bg-primary-light/20 transition-colors'
                }`}
                aria-expanded={openDropdown === index}
              >
                {item.title}
                <ChevronDown 
                  className={`ml-1 h-4 w-4 transition-transform duration-200 ${
                    openDropdown === index ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              
              {openDropdown === index && (
                <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 animate-slideDown">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    {item.dropdownItems.map((dropdownItem, dropdownIndex) => (
                      <Link
                        key={dropdownIndex}
                        to={dropdownItem.path}
                        className={`block px-4 py-2 text-sm transition-colors ${
                          isActive(dropdownItem.path)
                            ? 'bg-primary-50 text-primary font-medium'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-primary'
                        }`}
                        role="menuitem"
                      >
                        {dropdownItem.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <Link
              to={item.path}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive(item.path) 
                  ? 'text-white bg-primary-dark' 
                  : 'text-white hover:bg-primary-light/20'
              }`}
            >
              {item.title}
            </Link>
          )}
        </div>
      ))}
    </nav>
  );
};

export default Navigation;